import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SiteShell from "../site/SiteShell";
import PageHero from "../site/PageHero";
import { Reveal } from "../site/motion";
import { NETWORK_TIERS, TRADES, REGIONS, TURNOVER_BANDS, PACKAGE_VALUES, CLIENT_TYPE_OPTIONS } from "../site/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INIT = {
  company_name: "",
  company_number: "",
  years_trading: "",
  turnover_band: "",
  employees: "",
  website: "",
  contact_name: "",
  email: "",
  phone: "",
  trades: [],
  coverage_regions: [],
  typical_package_value: "",
  largest_project: "",
  delivery_model: "",
  simultaneous_projects: "",
  typical_client_types: [],
  current_capacity: "",
  accreditations: [],
  notes: "",
};

export default function DeliveryPartners() {
  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggleArr = (field, val) =>
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(val) ? f[field].filter((v) => v !== val) : [...f[field], val],
    }));
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email) {
      toast.error("Please provide company name, contact name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/delivery-partners`, form);
      setDone(true);
      setForm(INIT);
      toast.success("Application received — thank you.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteShell>
      <PageHero
        overline="Delivery Partners"
        title="Join a trusted network of specialist construction businesses."
        kicker="Being good at construction isn't always enough. Larger clients expect strong Health & Safety, compliance, financial resilience and professional processes. Verified Workforce helps you bridge the gap and access bigger opportunities."
        image="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1800&q=70"
      >
        <div className="flex flex-wrap gap-3">
          <a href="#apply" className="vw-btn vw-btn--primary" data-testid="partners-apply-cta">
            Apply to the Network
            <ArrowUpRight size={16} />
          </a>
          <Link to="/readiness" className="vw-btn vw-btn--gold" data-testid="partners-readiness-cta">
            Check Your Contract Readiness
          </Link>
        </div>
      </PageHero>

      {/* Network tiers */}
      <section className="vw-section bg-[var(--vw-paper)]" data-testid="partners-tiers-section">
        <div className="vw-container">
          <Reveal>
            <div className="vw-overline mb-4">The Verified Delivery Network</div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05] max-w-3xl">
              There's a difference between being registered and being verified.
            </h2>
            <p className="mt-6 text-[var(--vw-muted)] max-w-3xl leading-relaxed">
              Our network uses four internal statuses. These are not statutory or
              externally accredited certifications — they describe where a contractor sits
              within the Verified Workforce supply-chain development framework.
            </p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {NETWORK_TIERS.map((t, i) => (
              <Reveal key={t.key} delay={i * 0.08}>
                <div className={`relative p-7 h-full border ${i === 3 ? "bg-[var(--vw-navy)] text-white border-[var(--vw-gold)]" : "bg-white border-[var(--vw-line)]"}`}>
                  <div className={`font-mono text-[11px] tracking-[0.22em] uppercase ${i === 3 ? "text-[var(--vw-gold)]" : "text-[var(--vw-blue)]"}`}>
                    Tier 0{i + 1}
                  </div>
                  <div className={`mt-4 font-display text-xl font-semibold ${i === 3 ? "text-white" : "text-[var(--vw-navy)]"}`}>
                    {t.label}
                  </div>
                  <p className={`mt-3 text-sm leading-relaxed ${i === 3 ? "text-white/70" : "text-[var(--vw-muted)]"}`}>{t.body}</p>
                  {i === 3 && (
                    <div className="mt-5 vw-badge vw-badge--gold">Manual Award Only</div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-xs text-[var(--vw-muted)] max-w-3xl">
            Preferred Delivery Partner status is only awarded manually by Verified Workforce
            following review and demonstrable delivery performance. It cannot be assigned
            automatically.
          </p>
        </div>
      </section>

      {/* Application */}
      <section id="apply" className="vw-section bg-white" data-testid="partners-apply-section">
        <div className="vw-container">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="sticky top-28">
                <Reveal>
                  <div className="vw-overline mb-4">Apply</div>
                  <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05]">
                    Register your business.
                  </h2>
                  <p className="mt-6 text-[var(--vw-muted)] leading-relaxed">
                    Tell us about your company, trades and capacity. We'll follow up to
                    review documentation and talk through your route to becoming a Verified
                    Delivery Partner.
                  </p>
                  <ul className="mt-6 space-y-2 text-sm text-[var(--vw-charcoal)]">
                    <li className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--vw-blue)]"/>Review within 2 working days</li>
                    <li className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--vw-blue)]"/>No cost to register interest</li>
                    <li className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--vw-blue)]"/>Compliance support available</li>
                  </ul>
                </Reveal>
              </div>
            </div>

            <div className="lg:col-span-8">
              <Reveal delay={0.08}>
                <div className="border border-[var(--vw-line)] bg-white p-8 lg:p-10">
                  {done ? (
                    <div className="text-center py-10" data-testid="partners-success">
                      <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[var(--vw-blue)]/10 text-[var(--vw-blue)] mb-6">
                        <CheckCircle2 size={30} />
                      </div>
                      <h3 className="font-display text-2xl font-semibold text-[var(--vw-navy)]">Application received</h3>
                      <p className="mt-3 text-[var(--vw-muted)]">
                        A member of the Verified Workforce team will review your application
                        and be in touch within 2 working days.
                      </p>
                      <Link to="/readiness" className="vw-btn vw-btn--outline-dark mt-8" data-testid="partners-readiness-after">
                        Take the Readiness Assessment
                      </Link>
                    </div>
                  ) : (
                    <form onSubmit={submit} data-testid="partners-form">
                      <SectionTitle index="01" title="Company" />
                      <div className="grid sm:grid-cols-2 gap-5">
                        <Input name="company_name" label="Company Name *" value={form.company_name} onChange={onChange} required testid="pf-company" />
                        <Input name="company_number" label="Companies House Number" value={form.company_number} onChange={onChange} testid="pf-company-no" />
                        <Input name="years_trading" label="Years Trading" value={form.years_trading} onChange={onChange} testid="pf-years" />
                        <Select name="turnover_band" label="Annual Turnover" value={form.turnover_band} onChange={onChange} options={TURNOVER_BANDS} testid="pf-turnover" />
                        <Input name="employees" label="Number of Employees" value={form.employees} onChange={onChange} testid="pf-employees" />
                        <Input name="website" label="Website" value={form.website} onChange={onChange} testid="pf-website" />
                      </div>

                      <SectionTitle index="02" title="Primary Contact" />
                      <div className="grid sm:grid-cols-2 gap-5">
                        <Input name="contact_name" label="Full Name *" value={form.contact_name} onChange={onChange} required testid="pf-contact" />
                        <Input name="email" label="Email *" type="email" value={form.email} onChange={onChange} required testid="pf-email" />
                        <Input name="phone" label="Telephone" value={form.phone} onChange={onChange} testid="pf-phone" />
                      </div>

                      <SectionTitle index="03" title="Trades & Coverage" />
                      <CheckGroup
                        title="Primary Trades"
                        items={TRADES}
                        values={form.trades}
                        onToggle={(v) => toggleArr("trades", v)}
                        testid="pf-trades"
                      />
                      <div className="mt-6">
                        <CheckGroup
                          title="Coverage Regions"
                          items={REGIONS}
                          values={form.coverage_regions}
                          onToggle={(v) => toggleArr("coverage_regions", v)}
                          testid="pf-regions"
                        />
                      </div>

                      <SectionTitle index="04" title="Commercial Capability" />
                      <div className="grid sm:grid-cols-2 gap-5">
                        <Select name="typical_package_value" label="Typical Package Value" value={form.typical_package_value} onChange={onChange} options={PACKAGE_VALUES} testid="pf-value" />
                        <Input name="largest_project" label="Largest Project Completed" value={form.largest_project} onChange={onChange} testid="pf-largest" />
                        <Select name="delivery_model" label="Delivery Model" value={form.delivery_model} onChange={onChange} options={["Labour-only", "Supply-and-fit", "Both"]} testid="pf-model" />
                        <Input name="simultaneous_projects" label="Projects Delivered Simultaneously" value={form.simultaneous_projects} onChange={onChange} testid="pf-simul" />
                        <Input name="current_capacity" label="Current Capacity" value={form.current_capacity} onChange={onChange} testid="pf-capacity" placeholder="e.g. 60% utilised, 3 crews available" />
                      </div>
                      <div className="mt-6">
                        <CheckGroup
                          title="Typical Client Types"
                          items={CLIENT_TYPE_OPTIONS}
                          values={form.typical_client_types}
                          onToggle={(v) => toggleArr("typical_client_types", v)}
                          testid="pf-clients"
                        />
                      </div>

                      <SectionTitle index="05" title="Accreditations (current)" />
                      <CheckGroup
                        title="Please tick any current accreditations"
                        items={["SSIP","CHAS","SMAS","SafeContractor","Constructionline","ISO 9001","ISO 14001","ISO 45001","NICEIC","Gas Safe","Other"]}
                        values={form.accreditations}
                        onToggle={(v) => toggleArr("accreditations", v)}
                        testid="pf-accreditations"
                      />

                      <div className="mt-6 vw-field">
                        <label htmlFor="pf-notes">Anything else</label>
                        <textarea
                          id="pf-notes"
                          name="notes"
                          rows={3}
                          value={form.notes}
                          onChange={onChange}
                          data-testid="pf-notes"
                          placeholder="Recent key projects, availability, special capabilities…"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="vw-btn vw-btn--primary mt-8"
                        data-testid="partners-submit-btn"
                      >
                        {loading ? <><Loader2 size={16} className="animate-spin"/> Submitting…</> : "Submit Application"}
                      </button>
                      <p className="mt-4 text-xs text-[var(--vw-muted)]">
                        By submitting this form you agree to be contacted by Verified
                        Workforce regarding your application. We will not share your
                        details without permission.
                      </p>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

function SectionTitle({ index, title }) {
  return (
    <div className="mt-10 first:mt-0 mb-5 flex items-center gap-3">
      <span className="font-mono text-[11px] tracking-[0.22em] text-[var(--vw-gold)]">
        {index}
      </span>
      <h3 className="font-display text-lg font-semibold text-[var(--vw-navy)] uppercase tracking-wide">
        {title}
      </h3>
      <span className="h-px flex-1 bg-[var(--vw-line)]" />
    </div>
  );
}

function Input({ label, name, value, onChange, type = "text", required, placeholder, testid }) {
  const id = `field-${testid || name}`;
  return (
    <div className="vw-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

function Select({ label, name, value, onChange, options, testid }) {
  const id = `field-${testid || name}`;
  return (
    <div className="vw-field">
      <label htmlFor={id}>{label}</label>
      <select id={id} name={name} value={value} onChange={onChange} data-testid={testid}>
        <option value="">Select…</option>
        {options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </div>
  );
}

function CheckGroup({ title, items, values, onToggle, testid }) {
  return (
    <div>
      <label className="block mb-3 font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--vw-navy)]">{title}</label>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((t) => {
          const active = values.includes(t);
          return (
            <label key={t} className={`vw-check ${active ? "vw-check--active" : ""}`} data-testid={`${testid}-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <input type="checkbox" checked={active} onChange={() => onToggle(t)} />
              <span>{t}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
